import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Clock, Trash2, UserPlus, Pencil, FileDown, Shield, ArrowLeft, User, Briefcase, CreditCard, GraduationCap, Save } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const MODULE_LABELS = {
  dashboard: 'Dashboard', einsatzplanung: 'Einsatzplanung', projekte: 'Projekte',
  mitarbeiter: 'Mitarbeiter', abwesenheiten: 'Abwesenheiten', dokumente: 'Dokumente',
  finanzen: 'Finanzen', kunden: 'Kunden', lager: 'Lager', materialien: 'Materialien',
  rechnungsposten: 'Rechnungsposten', einstellungen: 'Einstellungen', kalender: 'Kalender'
};

export default function Mitarbeiter() {
  const { API, user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showTime, setShowTime] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPerms, setShowPerms] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsRole, setPermsRole] = useState('');
  const [customPerms, setCustomPerms] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileTab, setProfileTab] = useState('personal');
  const [timeForm, setTimeForm] = useState({ project_id: '', date: '', start_time: '08:00', end_time: '16:00', break_minutes: 30, notes: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'mitarbeiter' });
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });

  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'projektleiter';

  useEffect(() => {
    Promise.all([axios.get(`${API}/users`), axios.get(`${API}/projects`)])
      .then(([u, p]) => { setUsers(u.data); setProjects(p.data); });
  }, [API]);

  const openDetail = async (u) => {
    setDetailUser(u);
    try {
      const res = await axios.get(`${API}/users/${u.id}/profile`);
      setProfile(res.data || {});
    } catch { setProfile({}); }
    setProfileTab('personal');
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await axios.put(`${API}/users/${detailUser.id}/profile`, profile);
      setProfile(res.data);
      toast.success('Kartei gespeichert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setProfileSaving(false); }
  };

  const updateProfile = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const loadTimeEntries = async (userId) => {
    setSelectedUser(userId);
    const res = await axios.get(`${API}/time-entries?user_id=${userId}`);
    setEntries(res.data);
  };

  const handleCreateUser = async () => {
    try {
      const res = await axios.post(`${API}/users`, userForm);
      setUsers(prev => [...prev, res.data]);
      setShowCreate(false);
      setUserForm({ name: '', email: '', password: '', role: 'mitarbeiter' });
      toast.success('Mitarbeiter angelegt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (id === currentUser?.id) {
      toast.error('Eigenes Konto kann nicht gelöscht werden');
      return;
    }
    try {
      await axios.delete(`${API}/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
      if (selectedUser === id) { setSelectedUser(null); setEntries([]); }
      toast.success(`${name} wurde gelöscht`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, email: u.email, role: u.role });
    setShowEdit(true);
  };

  const handleEditUser = async () => {
    try {
      const res = await axios.put(`${API}/users/${editingUser.id}`, editForm);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? res.data : u));
      setShowEdit(false);
      setEditingUser(null);
      toast.success('Mitarbeiter aktualisiert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const openPerms = async (u) => {
    setPermsUser(u);
    setPermsRole(u.role || 'mitarbeiter');
    try {
      const res = await axios.get(`${API}/users/${u.id}/permissions`);
      setCustomPerms(res.data.custom_permissions || res.data.permissions || {});
    } catch {
      setCustomPerms(u.permissions || {});
    }
    setShowPerms(true);
  };

  const handleSavePerms = async () => {
    try {
      const body = { role: permsRole };
      if (permsRole === 'benutzerdefiniert') body.custom_permissions = customPerms;
      const res = await axios.put(`${API}/users/${permsUser.id}/permissions`, body);
      setUsers(prev => prev.map(u => u.id === permsUser.id ? res.data : u));
      setShowPerms(false);
      toast.success('Berechtigungen gespeichert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const togglePerm = (module, type) => {
    setCustomPerms(prev => ({
      ...prev,
      [module]: { ...prev[module], [type]: !prev[module]?.[type] }
    }));
  };

  const handleCreateEntry = async () => {
    try {
      const res = await axios.post(`${API}/time-entries`, timeForm);
      setEntries(prev => [...prev, res.data]);
      setShowTime(false);
      setTimeForm({ project_id: '', date: '', start_time: '08:00', end_time: '16:00', break_minutes: 30, notes: '' });
      toast.success('Zeiteintrag erstellt');
    } catch { toast.error('Fehler'); }
  };

  const handleDeleteEntry = async (id) => {
    await axios.delete(`${API}/time-entries/${id}`);
    setEntries(prev => prev.filter(e => e.id !== id));
    toast.success('Gelöscht');
  };

  const roleColors = { admin: 'text-rose-400 bg-rose-400/10 border-rose-400/20', projektleiter: 'text-blue-400 bg-blue-400/10 border-blue-400/20', mitarbeiter: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', benutzerdefiniert: 'text-violet-400 bg-violet-400/10 border-violet-400/20' };
  const roleLabels = { admin: 'Administrator', projektleiter: 'Projektleiter', mitarbeiter: 'Mitarbeiter', benutzerdefiniert: 'Benutzerdefiniert' };

  const renderPField = (label, field, type = 'text', placeholder = '') => (
    <div>
      <Label className="text-slate-400 text-xs uppercase tracking-wider">{label}</Label>
      <Input value={profile[field] || ''} onChange={e => updateProfile(field, type === 'number' ? (parseFloat(e.target.value) || '') : e.target.value)}
        type={type} placeholder={placeholder} disabled={!canManage}
        className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50 text-sm" />
    </div>
  );

  // ─── Kartei Detailansicht ───
  if (detailUser) {
    return (
      <div className="space-y-4 animate-fade-in" data-testid="employee-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setDetailUser(null)} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-sm bg-amber-500 flex items-center justify-center text-slate-950 font-bold text-lg">
            {detailUser.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{detailUser.name}</h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">{detailUser.email}</span>
              <Badge className={`${roleColors[detailUser.role] || ''} rounded-sm text-xs`}>{roleLabels[detailUser.role] || detailUser.role}</Badge>
            </div>
          </div>
          {canManage && (
            <Button onClick={saveProfile} disabled={profileSaving} className="ml-auto bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              <Save className="w-4 h-4 mr-2" />{profileSaving ? 'Speichern...' : 'Kartei speichern'}
            </Button>
          )}
        </div>

        <Tabs value={profileTab} onValueChange={setProfileTab}>
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="personal" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><User className="w-4 h-4 mr-1.5" />Persönlich</TabsTrigger>
            <TabsTrigger value="employment" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><Briefcase className="w-4 h-4 mr-1.5" />Beschäftigung</TabsTrigger>
            <TabsTrigger value="finance" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><CreditCard className="w-4 h-4 mr-1.5" />Steuer & Bank</TabsTrigger>
            <TabsTrigger value="qualifications" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><GraduationCap className="w-4 h-4 mr-1.5" />Qualifikationen</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Persönliche Daten</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Vorname", "first_name", "text", "Max")}
                {renderPField("Nachname", "last_name", "text", "Mustermann")}
              </div>
              <div className="grid grid-cols-3 gap-4">
                {renderPField("Geburtsdatum", "date_of_birth", "date")}
                {renderPField("Geburtsort", "place_of_birth", "text", "Berlin")}
                {renderPField("Nationalität", "nationality", "text", "Deutsch")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Geschlecht</Label>
                  <Select value={profile.gender || ''} onValueChange={v => updateProfile('gender', v)} disabled={!canManage}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="männlich" className="text-slate-200">Männlich</SelectItem>
                      <SelectItem value="weiblich" className="text-slate-200">Weiblich</SelectItem>
                      <SelectItem value="divers" className="text-slate-200">Divers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Familienstand</Label>
                  <Select value={profile.marital_status || ''} onValueChange={v => updateProfile('marital_status', v)} disabled={!canManage}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="ledig" className="text-slate-200">Ledig</SelectItem>
                      <SelectItem value="verheiratet" className="text-slate-200">Verheiratet</SelectItem>
                      <SelectItem value="geschieden" className="text-slate-200">Geschieden</SelectItem>
                      <SelectItem value="verwitwet" className="text-slate-200">Verwitwet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Adresse</h3>
              {renderPField("Straße + Hausnummer", "street", "text", "Musterstr. 1")}
              <div className="grid grid-cols-3 gap-4">
                {renderPField("PLZ", "zip_code", "text", "10115")}
                {renderPField("Stadt", "city", "text", "Berlin")}
                {renderPField("Land", "country", "text", "Deutschland")}
              </div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Kontakt</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Telefon", "phone", "text", "030 12345678")}
                {renderPField("Mobil", "mobile", "text", "0170 1234567")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Notfallkontakt", "emergency_contact", "text", "Name, Beziehung")}
                {renderPField("Notfall-Telefon", "emergency_phone", "text", "0170 9876543")}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="employment" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Beschäftigung</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Position / Berufsbezeichnung", "position", "text", "Geselle, Meister, Helfer...")}
                {renderPField("Abteilung", "department", "text", "Montage, Büro...")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Eintrittsdatum", "entry_date", "date")}
                {renderPField("Austrittsdatum", "exit_date", "date")}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Vertragsart</Label>
                  <Select value={profile.contract_type || ''} onValueChange={v => updateProfile('contract_type', v)} disabled={!canManage}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="vollzeit" className="text-slate-200">Vollzeit</SelectItem>
                      <SelectItem value="teilzeit" className="text-slate-200">Teilzeit</SelectItem>
                      <SelectItem value="minijob" className="text-slate-200">Minijob</SelectItem>
                      <SelectItem value="befristet" className="text-slate-200">Befristet</SelectItem>
                      <SelectItem value="unbefristet" className="text-slate-200">Unbefristet</SelectItem>
                      <SelectItem value="ausbildung" className="text-slate-200">Ausbildung</SelectItem>
                      <SelectItem value="praktikum" className="text-slate-200">Praktikum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {renderPField("Wochenstunden", "work_hours_per_week", "number", "40")}
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Gehaltstyp</Label>
                  <Select value={profile.salary_type || ''} onValueChange={v => updateProfile('salary_type', v)} disabled={!canManage}>
                    <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1 disabled:opacity-50"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="monatlich" className="text-slate-200">Monatlich</SelectItem>
                      <SelectItem value="stündlich" className="text-slate-200">Stündlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isAdmin && renderPField("Gehalt (EUR)", "salary", "number", "3500")}
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Arbeitszeiten</h3>
              <div className="grid grid-cols-3 gap-4">
                {renderPField("Arbeitsbeginn", "work_start", "time")}
                {renderPField("Arbeitsende", "work_end", "time")}
                {renderPField("Pause (Minuten)", "break_minutes", "number", "30")}
              </div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Urlaub</h3>
              {renderPField("Urlaubstage pro Jahr", "vacation_days", "number", "30")}
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Sonstiges</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("T-Shirt Größe", "shirt_size", "text", "M, L, XL...")}
                {renderPField("Schuhgröße", "shoe_size", "text", "42")}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="finance" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Steuer</h3>
              <div className="grid grid-cols-3 gap-4">
                {renderPField("Steuer-ID", "tax_id", "text", "12 345 678 901")}
                {renderPField("Steuerklasse", "tax_class", "text", "1-6")}
                {renderPField("SV-Nummer", "social_security_number", "text", "12 345678 A 123")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderPField("Krankenkasse", "health_insurance", "text", "AOK, TK, Barmer...")}
                {renderPField("Versichertennummer", "health_insurance_number")}
              </div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider pt-2">Bankverbindung</h3>
              <div className="grid grid-cols-3 gap-4">
                {renderPField("IBAN", "iban", "text", "DE89 3704 0044 0532 0130 00")}
                {renderPField("BIC", "bic", "text", "COBADEFFXXX")}
                {renderPField("Bank", "bank_name", "text", "Commerzbank")}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="qualifications" className="mt-4">
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Qualifikationen & Zertifikate</h3>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Qualifikationen / Ausbildung</Label>
                <Textarea value={profile.qualifications || ''} onChange={e => updateProfile('qualifications', e.target.value)} disabled={!canManage}
                  placeholder="Gesellenbrief, Meisterbrief, Studium..." rows={3}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Zertifikate / Scheine</Label>
                <Textarea value={profile.certifications || ''} onChange={e => updateProfile('certifications', e.target.value)} disabled={!canManage}
                  placeholder="Staplerschein, Schweißerschein, Erste-Hilfe, Asbestschein..." rows={3}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" />
              </div>
              {renderPField("Führerschein", "drivers_license", "text", "B, BE, C, CE...")}
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Notizen</Label>
                <Textarea value={profile.notes || ''} onChange={e => updateProfile('notes', e.target.value)} disabled={!canManage}
                  placeholder="Sonstige Anmerkungen..." rows={3}
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1 disabled:opacity-50" />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="mitarbeiter-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Mitarbeiter</h1>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => window.open(`${API}/export/employees`, '_blank')}
            className="bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 rounded-sm">
            <FileDown className="w-4 h-4 mr-1" />Export
          </Button>
          <Button onClick={() => setShowTime(true)} data-testid="add-time-entry"
            className="bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm">
            <Clock className="w-4 h-4 mr-2" />Zeiterfassung
          </Button>
          {canManage && (
            <Button onClick={() => setShowCreate(true)} data-testid="create-employee-button"
              className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
              <UserPlus className="w-4 h-4 mr-2" />Neuer Mitarbeiter
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map(u => (
          <div key={u.id} data-testid={`employee-card-${u.id}`}
            className={`bg-slate-900/50 border rounded-sm p-5 group cursor-pointer hover:border-slate-700 transition-colors ${selectedUser === u.id ? 'border-amber-500/50' : 'border-slate-800/60'}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3 flex-1 min-w-0" onClick={() => openDetail(u)}>
                <div className="w-10 h-10 rounded-sm bg-amber-500 flex items-center justify-center text-slate-950 font-bold shrink-0">
                  {u.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
              </div>
              {canManage && (
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                    data-testid={`edit-employee-${u.id}`}
                    className="w-8 h-8 text-slate-500 hover:text-amber-400">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openPerms(u); }}
                      data-testid={`perms-employee-${u.id}`}
                      className="w-8 h-8 text-slate-500 hover:text-violet-400">
                      <Shield className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {u.id !== currentUser?.id && (
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id, u.name); }}
                      data-testid={`delete-employee-${u.id}`}
                      className="w-8 h-8 text-slate-500 hover:text-rose-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Badge className={`${roleColors[u.role] || ''} rounded-sm text-xs`}>{roleLabels[u.role] || u.role}</Badge>
          </div>
        ))}
      </div>

      {selectedUser && (
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6" data-testid="time-entries-section">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
            Zeiterfassung — {users.find(u => u.id === selectedUser)?.name}
          </h2>
          {entries.length ? (
            <div className="space-y-2">
              {entries.map(e => (
                <div key={e.id} className="flex items-center justify-between py-3 px-4 bg-slate-950/50 rounded-sm border border-slate-800/40" data-testid={`time-entry-${e.id}`}>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono text-amber-400">{e.date}</span>
                    <span className="text-sm text-slate-300">{e.start_time} - {e.end_time}</span>
                    <span className="text-xs text-slate-500">Pause: {e.break_minutes}min</span>
                    {e.notes && <span className="text-xs text-slate-400">{e.notes}</span>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteEntry(e.id)} className="text-slate-400 hover:text-rose-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">Keine Einträge</p>}
        </div>
      )}

      {/* Neuer Mitarbeiter Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Neuer Mitarbeiter</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input data-testid="new-employee-name" value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Vorname Nachname" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">E-Mail</Label>
              <Input data-testid="new-employee-email" type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="mitarbeiter@firma.de" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Passwort</Label>
              <Input data-testid="new-employee-password" type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="Anfangspasswort" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Rolle</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="new-employee-role" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="mitarbeiter" className="text-slate-200">Mitarbeiter</SelectItem>
                  <SelectItem value="projektleiter" className="text-slate-200">Projektleiter</SelectItem>
                  <SelectItem value="admin" className="text-slate-200">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateUser} data-testid="submit-new-employee"
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              Mitarbeiter anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zeiterfassung Dialog */}
      <Dialog open={showTime} onOpenChange={setShowTime}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Neuer Zeiteintrag</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Datum</Label>
              <Input data-testid="time-date" type="date" value={timeForm.date} onChange={e => setTimeForm({ ...timeForm, date: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Projekt (optional)</Label>
              <Select value={timeForm.project_id} onValueChange={v => setTimeForm({ ...timeForm, project_id: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-slate-200">{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Von</Label>
                <Input data-testid="time-start" type="time" value={timeForm.start_time} onChange={e => setTimeForm({ ...timeForm, start_time: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Bis</Label>
                <Input data-testid="time-end" type="time" value={timeForm.end_time} onChange={e => setTimeForm({ ...timeForm, end_time: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Pause (Minuten)</Label>
              <Input data-testid="time-break" type="number" value={timeForm.break_minutes} onChange={e => setTimeForm({ ...timeForm, break_minutes: parseInt(e.target.value) || 0 })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <Button onClick={handleCreateEntry} data-testid="create-time-entry-submit" className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              Eintrag erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mitarbeiter bearbeiten Dialog */}
      <Dialog open={showEdit} onOpenChange={v => { setShowEdit(v); if (!v) setEditingUser(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Mitarbeiter bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input data-testid="edit-employee-name" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">E-Mail</Label>
              <Input data-testid="edit-employee-email" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Rolle</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-employee-role" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="mitarbeiter" className="text-slate-200">Mitarbeiter</SelectItem>
                  <SelectItem value="projektleiter" className="text-slate-200">Projektleiter</SelectItem>
                  <SelectItem value="admin" className="text-slate-200">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleEditUser} data-testid="save-edit-employee"
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              Änderungen speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Berechtigungs-Dialog */}
      <Dialog open={showPerms} onOpenChange={v => { setShowPerms(v); if (!v) setPermsUser(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-400" />
              Berechtigungen — {permsUser?.name}
            </DialogTitle>
          </DialogHeader>
          {permsUser && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Rollen-Template</Label>
                <Select value={permsRole} onValueChange={v => {
                  setPermsRole(v);
                  if (v !== 'benutzerdefiniert') {
                    // Load template permissions for preview
                    axios.get(`${API}/permissions/templates`).then(r => {
                      setCustomPerms(r.data.roles[v] || {});
                    }).catch(() => {});
                  }
                }}>
                  <SelectTrigger data-testid="perm-role-select" className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="admin" className="text-slate-200">Administrator — Voller Zugriff</SelectItem>
                    <SelectItem value="projektleiter" className="text-slate-200">Projektleiter — Alles außer Einstellungen & Finanzen</SelectItem>
                    <SelectItem value="mitarbeiter" className="text-slate-200">Mitarbeiter — Nur eigene Daten + Chat</SelectItem>
                    <SelectItem value="benutzerdefiniert" className="text-slate-200">Benutzerdefiniert — Individuell konfigurieren</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {permsRole !== 'admin' && (
                <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm overflow-hidden">
                  <div className="grid grid-cols-[1fr_70px_70px] gap-2 px-4 py-2 bg-slate-900/80 text-xs font-mono uppercase text-slate-500 tracking-wider border-b border-slate-800/40">
                    <span>Modul</span><span className="text-center">Lesen</span><span className="text-center">Schreiben</span>
                  </div>
                  {Object.entries(MODULE_LABELS).map(([mod, label]) => {
                    const r = customPerms[mod]?.read ?? false;
                    const w = customPerms[mod]?.write ?? false;
                    const isCustom = permsRole === 'benutzerdefiniert';
                    return (
                      <div key={mod} className="grid grid-cols-[1fr_70px_70px] gap-2 px-4 py-2.5 border-b border-slate-800/30 items-center">
                        <span className="text-sm text-slate-300">{label}</span>
                        <div className="flex justify-center">
                          <Switch checked={r} disabled={!isCustom} onCheckedChange={() => isCustom && togglePerm(mod, 'read')}
                            className="data-[state=checked]:bg-emerald-500" />
                        </div>
                        <div className="flex justify-center">
                          <Switch checked={w} disabled={!isCustom} onCheckedChange={() => isCustom && togglePerm(mod, 'write')}
                            className="data-[state=checked]:bg-amber-500" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {permsRole === 'admin' && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3 text-sm text-emerald-300">
                  Administrator hat vollen Zugriff auf alle Module.
                </div>
              )}

              <Button onClick={handleSavePerms} data-testid="save-permissions-btn"
                className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
                Berechtigungen speichern
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
